import { useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Plane, ShieldCheck, ArrowRight, FileCheck, Banknote, MessageCircle, Repeat, MapPin, Zap, Lock, Users, Send, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const DAKAR_PHONE = "221774419060";
const DAKAR_PHONE_DISPLAY = "+221 77 441 90 60";

function waLink(msg) {
  return `https://wa.me/${DAKAR_PHONE}?text=${encodeURIComponent(msg)}`;
}

export default function Transfer() {
  return (
    <div data-testid="transfer-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[var(--ink)] via-[#0a1428] to-[var(--secondary)] text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #FF3B30 0, transparent 45%), radial-gradient(circle at 80% 80%, #003366 0, transparent 45%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <p className="kicker !text-[var(--accent)]">Service Négosino</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter mt-4 max-w-4xl">
            Transférez votre argent <br />
            <span className="text-[var(--accent)]">en toute sécurité</span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 mt-6 max-w-2xl">
            Sans passer par les banques, sans frais cachés. Déposez à Dakar, récupérez en Chine.
            Un service simple et fiable pour les commerçants et voyageurs sénégalais.
          </p>

          <div className="flex flex-wrap gap-3 mt-10">
            <a
              href={waLink("Bonjour, je souhaite en savoir plus sur votre service de transfert d'argent Sénégal ↔ Chine.")}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] hover:bg-[#1ea855] text-white font-bold px-6 py-3 rounded inline-flex items-center gap-2"
              data-testid="transfer-wa-cta"
            >
              <MessageCircle size={18} /> Demander un transfert
            </a>
            <Link
              to="/a-propos"
              className="btn-outline !text-white !border-white/30 hover:!border-white"
              data-testid="transfer-team-cta"
            >
              Rencontrer l'équipe Dakar <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Two services */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid md:grid-cols-2 gap-6">
          <ServiceCard
            testid="service-cash"
            badge="Le plus demandé"
            icon={Plane}
            title="Cash à l'arrivée en Chine"
            subtitle="Voyagez léger, retirez à Yiwu ou Guangzhou"
            description="Vous partez faire vos achats en Chine ? Déposez votre argent au bureau Négosino de Dakar. Vous recevez une décharge officielle. À votre arrivée en Chine, notre équipe vous remet l'équivalent en cash (CNY) — pas de banque, pas de délai."
            bullets={[
              "Dépôt en FCFA, retrait en CNY (yuan)",
              "Décharge signée dès le dépôt",
              "Cash disponible dès votre arrivée à Yiwu ou Guangzhou",
              "Taux de change transparent, sans frais bancaires",
            ]}
            cta="Réserver un transfert Chine"
            waMsg="Bonjour, je voudrais déposer de l'argent au bureau Dakar pour le récupérer en Chine. Merci de me contacter."
          />
          <ServiceCard
            testid="service-exchange"
            badge="Change de devises"
            icon={Repeat}
            title="Change de devises"
            subtitle="EUR · USD · CNY · FCFA"
            description="Besoin de convertir des devises pour vos commandes ou vos voyages ? Déposez votre argent au bureau Dakar et récupérez la devise de votre choix aux meilleurs taux du marché."
            bullets={[
              "Change FCFA / EUR / USD / CNY",
              "Meilleurs taux garantis",
              "Opération réalisée en agence à Dakar",
              "Reçu officiel systématique",
            ]}
            cta="Demander un change"
            waMsg="Bonjour, j'aimerais faire un change de devises. Merci de m'indiquer les taux actuels."
          />
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[var(--bg-soft)] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="kicker !text-[var(--secondary)]">Comment ça marche</p>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-3">
            3 étapes. <span className="text-[var(--accent)]">Zéro tracas.</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Step n={1} icon={Wallet} title="Dépôt à Dakar" text="Rendez-vous au bureau Négosino Dakar avec le montant que vous souhaitez transférer. Notre équipe (Lala, Marème) vous accueille." />
            <Step n={2} icon={FileCheck} title="Décharge signée" text="Vous recevez une décharge officielle mentionnant le montant, la devise cible et le lieu de retrait." />
            <Step n={3} icon={Banknote} title="Cash à l'arrivée" text="À votre arrivée en Chine (Yiwu, Guangzhou) ou au bureau Dakar pour un change, récupérez votre argent en main propre." />
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <p className="kicker !text-[var(--secondary)]">Pourquoi Négosino</p>
        <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-3">
          Confiance. Rapidité. <span className="text-[var(--accent)]">Transparence.</span>
        </h2>

        <div className="grid md:grid-cols-4 gap-6 mt-12">
          <TrustCard icon={ShieldCheck} title="Bureau physique" text="Bureau ouvert à Dakar avec équipe dédiée. Aucune opération anonyme, tout est traçable." />
          <TrustCard icon={Zap} title="Sans délai" text="Cash disponible dès votre arrivée en Chine. Pas de virement à attendre." />
          <TrustCard icon={Lock} title="Taux transparents" text="Aucun frais caché. Vous connaissez le taux avant de déposer votre argent." />
          <TrustCard icon={Users} title="Équipe dédiée" text="Ibrahima, Fatima, Lala et Marème à Dakar ; B. Kane et A. Karim en Chine. Tous joignables." />
        </div>
      </section>

      {/* Request form */}
      <section className="bg-[var(--bg-soft)] py-20" id="demande">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <p className="kicker !text-[var(--secondary)]">Demande en ligne</p>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-3">
            Faites votre demande. <span className="text-[var(--accent)]">On vous rappelle.</span>
          </h2>
          <p className="text-[var(--ink-soft)] mt-4">
            Remplissez ce formulaire — Lala vous contacte sur WhatsApp dans les meilleurs délais avec les taux du jour et le rendez-vous au bureau Dakar.
          </p>
          <TransferRequestForm />
        </div>
      </section>

      {/* Contact */}
      <section className="bg-[var(--ink)] text-white py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter">
            Prêt à envoyer votre argent <br />
            <span className="text-[var(--accent)]">sans passer par la banque ?</span>
          </h2>
          <p className="text-white/70 mt-6 max-w-2xl mx-auto">
            Contactez Lala, notre responsable Finance & Transferts au bureau Dakar.
          </p>

          <div className="mt-10 card-flat !bg-white/5 !border-white/10 p-6 md:p-8 text-left inline-block">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#EC4899] to-[var(--accent)] flex items-center justify-center font-display font-black">L</div>
              <div>
                <p className="font-bold text-lg">Lala — Finance & Transferts</p>
                <p className="text-sm text-white/60">Bureau Négosino Dakar</p>
              </div>
            </div>
            <div className="mt-5 space-y-2">
              <a href={waLink("Bonjour Lala, je souhaite déposer de l'argent au bureau Dakar pour un transfert vers la Chine.")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#25D366] hover:text-[#1ea855] font-bold" data-testid="transfer-lala-wa">
                <MessageCircle size={16} /> WhatsApp Bureau Dakar
              </a>
              <a href={`tel:+${DAKAR_PHONE}`} className="flex items-center gap-2 text-white/80 hover:text-white">
                <MapPin size={16} /> {DAKAR_PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ testid, badge, icon: Icon, title, subtitle, description, bullets, cta, waMsg }) {
  return (
    <div className="card-flat p-8 flex flex-col" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="w-12 h-12 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] flex items-center justify-center flex-shrink-0">
          <Icon size={22} />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest bg-[var(--accent)]/10 text-[var(--accent)] px-2 py-1 rounded-full">{badge}</span>
      </div>
      <h3 className="font-display text-2xl md:text-3xl font-black mt-5">{title}</h3>
      <p className="text-sm text-[var(--ink-soft)] mt-1">{subtitle}</p>
      <p className="mt-4 text-[var(--ink-soft)]">{description}</p>
      <ul className="mt-6 space-y-2">
        {bullets.map((b) => (
          <li key={b} className="flex gap-2 text-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] mt-2 flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <a
        href={waLink(waMsg)}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary mt-8 justify-center"
      >
        {cta} <ArrowRight size={16} />
      </a>
    </div>
  );
}

function Step({ n, icon: Icon, title, text }) {
  return (
    <div className="card-flat p-6 relative">
      <span className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-[var(--accent)] text-white font-display font-black flex items-center justify-center">
        {n}
      </span>
      <Icon className="text-[var(--secondary)]" size={24} />
      <h3 className="font-display text-xl font-bold mt-4">{title}</h3>
      <p className="text-sm text-[var(--ink-soft)] mt-2">{text}</p>
    </div>
  );
}

function TrustCard({ icon: Icon, title, text }) {
  return (
    <div className="p-6 border-l-2 border-[var(--accent)]">
      <Icon className="text-[var(--accent)]" size={20} />
      <h3 className="font-display text-lg font-bold mt-3">{title}</h3>
      <p className="text-sm text-[var(--ink-soft)] mt-2">{text}</p>
    </div>
  );
}

function TransferRequestForm() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", amount: "",
    from_currency: "XOF", to_currency: "CNY", pickup_city: "Yiwu",
    kind: "cash_china", notes: "",
  });
  const [state, setState] = useState({ loading: false, err: "", ref: null });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, err: "", ref: null });
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (!payload.email) delete payload.email;
      const { data } = await api.post("/transfer-requests", payload);
      setState({ loading: false, err: "", ref: data.reference });
    } catch (e) {
      setState({ loading: false, err: formatApiError(e), ref: null });
    }
  };

  if (state.ref) {
    return (
      <div className="mt-8 card-flat p-8 text-center bg-green-50 border border-green-200" data-testid="transfer-request-success">
        <CheckCircle2 className="mx-auto text-green-600" size={48} />
        <p className="font-display text-2xl font-bold mt-4">Demande envoyée !</p>
        <p className="text-sm text-[var(--ink-soft)] mt-2">
          Référence : <span className="font-mono font-bold">{state.ref}</span>
        </p>
        <p className="text-[var(--ink-soft)] mt-3">
          Lala vous contactera sur WhatsApp très prochainement avec le taux du jour et le rendez-vous.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 card-flat p-6 md:p-8 space-y-4" data-testid="transfer-request-form">
      <div className="flex gap-2 flex-wrap">
        {[
          { k: "cash_china", l: "🛫 Cash à l'arrivée en Chine" },
          { k: "exchange", l: "🔄 Change de devises" },
        ].map((o) => (
          <button
            key={o.k}
            type="button"
            onClick={() => setForm({ ...form, kind: o.k })}
            className={`text-xs font-bold px-4 py-2 rounded-full transition-colors ${form.kind === o.k ? "bg-[var(--accent)] text-white" : "bg-[var(--bg-soft)] text-[var(--ink-soft)] hover:bg-black/5"}`}
            data-testid={`transfer-kind-${o.k}`}
          >
            {o.l}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <input required placeholder="Votre nom complet" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" data-testid="transfer-name" />
        <input required placeholder="Téléphone WhatsApp (+221...)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-field" data-testid="transfer-phone" />
        <input type="email" placeholder="Email (optionnel)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-field" data-testid="transfer-email" />
        <div className="flex gap-2">
          <input required type="number" step="1000" placeholder="Montant à transférer" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-field flex-1" data-testid="transfer-amount" />
          <select value={form.from_currency} onChange={(e) => setForm({ ...form, from_currency: e.target.value })} className="input-field w-24">
            <option value="XOF">FCFA</option>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
          </select>
        </div>
        {form.kind === "cash_china" ? (
          <>
            <select value={form.to_currency} onChange={(e) => setForm({ ...form, to_currency: e.target.value })} className="input-field" data-testid="transfer-to-currency">
              <option value="CNY">Recevoir en CNY (Yuan)</option>
              <option value="USD">Recevoir en USD</option>
              <option value="EUR">Recevoir en EUR</option>
            </select>
            <select value={form.pickup_city} onChange={(e) => setForm({ ...form, pickup_city: e.target.value })} className="input-field" data-testid="transfer-city">
              <option value="Yiwu">Retrait à Yiwu</option>
              <option value="Guangzhou">Retrait à Guangzhou</option>
              <option value="Autre">Autre ville (précisez en note)</option>
            </select>
          </>
        ) : (
          <select value={form.to_currency} onChange={(e) => setForm({ ...form, to_currency: e.target.value })} className="input-field md:col-span-2">
            <option value="EUR">Change vers EUR</option>
            <option value="USD">Change vers USD</option>
            <option value="CNY">Change vers CNY</option>
            <option value="XOF">Change vers FCFA</option>
          </select>
        )}
      </div>

      <textarea placeholder="Note ou question (optionnel)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-field min-h-[80px] w-full" data-testid="transfer-notes" />

      {state.err && <p className="text-sm text-red-500">{state.err}</p>}

      <button type="submit" disabled={state.loading} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="transfer-submit">
        {state.loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        Envoyer ma demande
      </button>
      <p className="text-[10px] text-[var(--ink-soft)] text-center">
        Aucun paiement en ligne. Lala vous contacte ensuite pour le rendez-vous au bureau Dakar.
      </p>
    </form>
  );
}
